import { useEffect, useRef } from 'react';
import '../assets/styles/EditeurRiche.css';

/**
 * Éditeur de texte enrichi, sans dépendance : un `contenteditable` piloté
 * par `document.execCommand`.
 *
 * Le choix de ne rien installer est délibéré. Les éditeurs du marché pèsent
 * plusieurs centaines de Ko et amènent leur propre modèle de document ;
 * ici on n'a besoin que de ce qu'un contrat imprime — gras, italique,
 * titres, listes, tableaux, liens. `execCommand` est déprécié sur le papier
 * mais implémenté par tous les navigateurs, et suffit à cela.
 *
 * La valeur est du HTML. Elle est nettoyée à l'entrée (chargement, collage)
 * avec la même liste blanche que le serveur (cf. articlesContrat.js) : le
 * serveur refiltre de toute façon au rendu, c'est lui qui fait foi ; le
 * nettoyage ici garde surtout la base propre d'un collage depuis Word.
 */

const BALISES_AUTORISEES = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's',
  'ul', 'ol', 'li',
  'h3', 'h4',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'a', 'blockquote',
]);
const BALISES_A_VIDER = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'noscript', 'template']);
const ATTRIBUTS_AUTORISES = { a: ['href'], td: ['colspan', 'rowspan'], th: ['colspan', 'rowspan'] };
const PROTOCOLES_AUTORISES = /^(https?:|mailto:|tel:)/i;

const echapper = (t) =>
  String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/**
 * Ramène un HTML quelconque à la liste blanche, en passant par le DOM
 * plutôt que par des expressions régulières : le navigateur sait analyser
 * du HTML mieux que nous.
 */
export function nettoyerHtml(html) {
  const bac = document.createElement('div');
  bac.innerHTML = html || '';

  const marcher = (noeud) => {
    for (const enfant of Array.from(noeud.childNodes)) {
      if (enfant.nodeType === Node.COMMENT_NODE) {
        enfant.remove();
        continue;
      }
      if (enfant.nodeType !== Node.ELEMENT_NODE) continue;

      const nom = enfant.tagName.toLowerCase();
      if (BALISES_A_VIDER.has(nom)) {
        enfant.remove();
        continue;
      }

      marcher(enfant);

      // Le <div> que produisent certains navigateurs à la touche Entrée est
      // un paragraphe qui s'ignore : on le renomme plutôt que le dissoudre,
      // sinon deux paragraphes se colleraient.
      if (nom === 'div') {
        const p = document.createElement('p');
        p.append(...Array.from(enfant.childNodes));
        enfant.replaceWith(p);
        continue;
      }
      if (!BALISES_AUTORISEES.has(nom)) {
        enfant.replaceWith(...Array.from(enfant.childNodes));
        continue;
      }

      const permis = ATTRIBUTS_AUTORISES[nom] || [];
      for (const attr of Array.from(enfant.attributes)) {
        const a = attr.name.toLowerCase();
        if (!permis.includes(a)) enfant.removeAttribute(attr.name);
        else if (a === 'href' && !PROTOCOLES_AUTORISES.test(attr.value.trim())) enfant.removeAttribute(attr.name);
      }
    }
  };
  marcher(bac);
  return bac.innerHTML;
}

const TABLEAU_VIDE =
  '<table><tbody>' +
  '<tr><td>&nbsp;</td><td>&nbsp;</td></tr>' +
  '<tr><td>&nbsp;</td><td>&nbsp;</td></tr>' +
  '</tbody></table><p><br></p>';

/**
 * @param {object}   p
 * @param {string}   p.value       HTML courant.
 * @param {Function} p.onChange    Reçoit le HTML à chaque frappe.
 * @param {boolean}  [p.disabled]
 * @param {string}   [p.placeholder]
 * @param {number}   [p.minHeight] En pixels.
 * @param {string}   [p.id]        Pour relier un <label>.
 */
function EditeurRiche({ value, onChange, disabled = false, placeholder = '', minHeight = 160, id }) {
  const ref = useRef(null);

  // Pendant la frappe, le DOM est la source de vérité : réécrire innerHTML à
  // chaque rendu renverrait le curseur au début. On ne l'écrit que quand la
  // valeur vient d'ailleurs — chargement, modèle appliqué — c'est-à-dire
  // quand elle diffère du DOM et que l'éditeur n'a pas le focus.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const voulu = value || '';
    if (el.innerHTML !== voulu && document.activeElement !== el) {
      el.innerHTML = nettoyerHtml(voulu);
    }
  }, [value]);

  useEffect(() => {
    // Entrée crée un <p>, pas un <div> : le gabarit du contrat stylise des <p>.
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch {
      /* navigateur sans cette commande : le nettoyage renomme les <div> */
    }
  }, []);

  const emettre = () => onChange?.(ref.current ? ref.current.innerHTML : '');

  /**
   * `onMouseDown` + preventDefault : le clic sur un bouton ne doit pas
   * retirer le focus à l'éditeur, sinon la sélection à mettre en gras
   * serait perdue avant que la commande s'applique.
   */
  const commande = (nom, arg = null) => (e) => {
    e.preventDefault();
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(nom, false, arg);
    emettre();
  };

  function lien(e) {
    e.preventDefault();
    if (disabled) return;
    ref.current?.focus();
    // window.prompt : rustique, mais synchrone — la sélection survit.
    const url = window.prompt('Adresse du lien (https://…)');
    if (!url) return;
    if (!PROTOCOLES_AUTORISES.test(url.trim())) {
      window.alert('Le lien doit commencer par https://, http://, mailto: ou tel:.');
      return;
    }
    document.execCommand('createLink', false, url.trim());
    emettre();
  }

  /**
   * Collage : on ne laisse pas le navigateur insérer le HTML brut — un
   * collage depuis Word apporte des centaines de balises et de styles. Le
   * HTML est nettoyé ; à défaut, le texte brut devient des paragraphes.
   */
  function coller(e) {
    e.preventDefault();
    if (disabled) return;
    const html = e.clipboardData.getData('text/html');
    const texte = e.clipboardData.getData('text/plain');
    const propre = html
      ? nettoyerHtml(html)
      : texte
          .split(/\r?\n/)
          .map((l) => `<p>${echapper(l) || '<br>'}</p>`)
          .join('');
    document.execCommand('insertHTML', false, propre);
    emettre();
  }

  const BOUTONS = [
    { libelle: 'G', titre: 'Gras', action: commande('bold'), classe: 'editeur-riche-gras' },
    { libelle: 'I', titre: 'Italique', action: commande('italic'), classe: 'editeur-riche-italique' },
    { libelle: 'S', titre: 'Souligné', action: commande('underline'), classe: 'editeur-riche-souligne' },
    { separateur: true },
    { libelle: 'Titre', titre: 'Sous-titre dans l’article', action: commande('formatBlock', 'h3') },
    { libelle: '¶', titre: 'Paragraphe', action: commande('formatBlock', 'p') },
    { separateur: true },
    { libelle: '• Liste', titre: 'Liste à puces', action: commande('insertUnorderedList') },
    { libelle: '1. Liste', titre: 'Liste numérotée', action: commande('insertOrderedList') },
    { libelle: 'Tableau', titre: 'Insérer un tableau 2 × 2', action: commande('insertHTML', TABLEAU_VIDE) },
    { libelle: 'Lien', titre: 'Insérer un lien', action: lien },
    { separateur: true },
    { libelle: '↶', titre: 'Annuler', action: commande('undo') },
    { libelle: '↷', titre: 'Rétablir', action: commande('redo') },
  ];

  return (
    <div className={`editeur-riche${disabled ? ' editeur-riche-desactive' : ''}`}>
      <div className="editeur-riche-barre" role="toolbar" aria-label="Mise en forme">
        {BOUTONS.map((b, i) =>
          b.separateur ? (
            <span className="editeur-riche-sep" key={`sep-${i}`} aria-hidden="true" />
          ) : (
            <button
              type="button"
              key={b.libelle}
              className={`editeur-riche-btn${b.classe ? ` ${b.classe}` : ''}`}
              title={b.titre}
              aria-label={b.titre}
              onMouseDown={b.action}
              disabled={disabled}
            >
              {b.libelle}
            </button>
          )
        )}
      </div>
      <div
        ref={ref}
        id={id}
        className="editeur-riche-zone"
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        style={{ minHeight }}
        onInput={emettre}
        onBlur={emettre}
        onPaste={coller}
      />
    </div>
  );
}

export default EditeurRiche;
